import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, KeyRound, Mail, Send } from 'lucide-react';
import { api } from '../api/client';
import AuthLayout from '../components/AuthLayout';
import ErrorBanner from '../components/ErrorBanner';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
      if (data?.devResetToken) {
        setDevResetUrl(`${window.location.origin}/reset-password?token=${data.devResetToken}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the email associated with your account and we'll send a reset link."
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="font-semibold text-brand-600 hover:underline">
            Back to sign in
          </Link>
        </>
      }
    >
      {submitted ? (
        <div className="space-y-4">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 flex-none mt-0.5" />
            <div>
              If an account exists for <span className="font-semibold">{email}</span>, a
              password reset link is on its way.
            </div>
          </div>
          {devResetUrl && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 text-amber-800 text-sm p-4 space-y-2">
              <p className="font-semibold flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Development mode
              </p>
              <p>Email delivery isn&apos;t configured. Use this link directly:</p>
              <a
                href={devResetUrl}
                className="block break-all font-mono text-xs text-brand-700 hover:underline"
              >
                {devResetUrl}
              </a>
            </div>
          )}
          <Link to="/login" className="btn-secondary w-full">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>
          </div>
          {error && <ErrorBanner body={error} onDismiss={() => setError('')} />}
          <button className="btn-primary w-full" disabled={loading}>
            {loading ? (
              'Sending…'
            ) : (
              <>
                <Send className="w-4 h-4" /> Send reset link
              </>
            )}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
